import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { getPortalToken, setPortalToken, clearPortalToken } from '../services/api';
import { isBiometricPromptActive, enableBiometric, updateBiometricToken, isBiometricEnabled, tryBiometricLogin } from '../services/biometric';
import { registerPush, unregisterPush } from '../services/push';
import { App as CapApp } from '@capacitor/app';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vergrendeld, setVergrendeld] = useState(false);

  const laadInfo = useCallback(async () => {
    if (!getPortalToken()) { setInfo(null); setLoading(false); return; }
    try {
      const res = await api.get('/klantportaal/info');
      setInfo(res.data);
      try { registerPush(); } catch (e) {}
    } catch (e) {
      setInfo(null);
      clearPortalToken();
    } finally {
      setLoading(false);
    }
  }, []);

  const init = useCallback(async () => {
    if (getPortalToken() && await isBiometricEnabled()) { setVergrendeld(true); setLoading(false); return; }
    await laadInfo();
  }, [laadInfo]);
  useEffect(() => { init(); }, [init]);

  useEffect(() => {
    let handle; let bgAt = 0;
    (async () => {
      try {
        handle = await CapApp.addListener('appStateChange', async ({ isActive }) => {
          if (isBiometricPromptActive()) return;
          if (!isActive) { bgAt = Date.now(); }
          else if (bgAt && Date.now() - bgAt > 60000 && await isBiometricEnabled()) { setVergrendeld(true); }
        });
      } catch (e) {}
    })();
    return () => { if (handle) handle.remove(); };
  }, []);

  const ontgrendel = async () => {
    const token = await tryBiometricLogin();
    if (token) {
      setPortalToken(token);
      setVergrendeld(false);
      setLoading(true);
      await laadInfo();
      return true;
    }
    return false;
  };

  const verwerkToken = async (token, metBiometric) => {
    setPortalToken(token);
    if (metBiometric) { await enableBiometric(token); }
    else if (await isBiometricEnabled()) { await updateBiometricToken(token); }
    await laadInfo();
  };

  const login = async (email, wachtwoord, metBiometric = false) => {
    const res = await api.post('/klantportaal/login', { email, wachtwoord });
    if (res.data.requires_mfa) {
      return { requiresMfa: true, mfaToken: res.data.mfa_token };
    }
    if (res.data.requires_mfa_setup) {
      return { requiresMfaSetup: true, setupToken: res.data.setup_token };
    }
    await verwerkToken(res.data.token, metBiometric);
    return { requiresMfa: false };
  };

  const loginMfa = async (mfaToken, code, metBiometric = false) => {
    const res = await api.post('/klantportaal/login-mfa', { mfa_token: mfaToken, code });
    await verwerkToken(res.data.token, metBiometric);
  };

  const loginMetToken = async (token) => {
    setPortalToken(token);
    await laadInfo();
    if (!getPortalToken()) throw new Error('Sessie verlopen');
  };

  const forcedSetupStart = async (setupToken) => {
    const res = await api.post('/klantportaal/2fa/forced-setup-start', { setup_token: setupToken });
    return { qr: res.data.qr, secret: res.data.secret };
  };

  const forcedSetupVerify = async (setupToken, code) => {
    const res = await api.post('/klantportaal/2fa/forced-setup-verify', { setup_token: setupToken, code });
    return { recoveryCodes: res.data.recovery_codes, token: res.data.token };
  };

  const voltooiMfaSetup = async (token, metBiometric = false) => {
    await verwerkToken(token, metBiometric);
  };

  const wachtwoordInstellen = async (token, wachtwoord) => {
    const res = await api.post('/klantportaal/wachtwoord-instellen', { token, wachtwoord });
    setPortalToken(res.data.token);
    await laadInfo();
  };

  const logout = async () => {
    try { if (!(await isBiometricEnabled())) { await api.post('/klantportaal/logout'); } } catch (e) {}
    try { unregisterPush(); } catch (e) {}
    clearPortalToken();
    setInfo(null);
    setVergrendeld(false);
    window.location.hash = '#/login';
  };

  const heeftRecht = (code) => Array.isArray(info?.rechten) && info.rechten.includes(code);

  return (
    <AuthContext.Provider value={{ info, loading, vergrendeld, ontgrendel, login, loginMfa, loginMetToken, forcedSetupStart, forcedSetupVerify, voltooiMfaSetup, wachtwoordInstellen, logout, heeftRecht, herlaad: laadInfo }}>
      {children}
    </AuthContext.Provider>
  );
}
